import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Handshake, Zap, Target, ArrowRight, CheckCircle, Users, Lightbulb, Shield, Award } from 'lucide-react';

const WhoWeAre = () => {
  const { language } = useLanguage();

  const content = {
    hero: {
      subtitle: language === 'fr' ? 'À PROPOS' : 'ABOUT US',
      title: language === 'fr' ? 'Qui Sommes-Nous' : 'Who We Are',
      tagline: language === 'fr' 
        ? "Un cabinet de conseil spécialisé pour CIOs naviguant des transformations complexes"
        : "A specialized advisory firm for CIOs navigating complex transformations"
    },
    intro: {
      description: language === 'fr'
        ? "Walaby est un cabinet de conseil en management spécialisé, focalisé sur le sourcing IT, la transformation des modèles opérationnels et le leadership de programmes complexes. Nous conseillons les CIOs et équipes de direction sur la structuration de leurs organisations IT, écosystèmes fournisseurs et programmes de transformation pour délivrer une valeur business durable."
        : "Walaby is a specialized management consulting firm focused on IT sourcing, operating model transformation and complex program leadership. We advise CIOs and executive leadership teams on how to structure their IT organizations, vendor ecosystems and transformation programs to deliver sustainable business value.",
      context: language === 'fr'
        ? "Dans un environnement où les écosystèmes technologiques deviennent de plus en plus complexes, les organisations ont besoin de plus que des frameworks de conseil génériques. Elles ont besoin d'une expertise focalisée et d'un conseil pragmatique ancré dans la réalité opérationnelle."
        : "In an environment where technology ecosystems are becoming increasingly complex, organizations need more than generic consulting frameworks. They need focused expertise and pragmatic advisory grounded in operational reality.",
      positioning: language === 'fr'
        ? "Walaby opère comme un cabinet de conseil hautement spécialisé, combinant une expertise approfondie en sourcing, modèles opérationnels et transformations à grande échelle."
        : "Walaby operates as a highly specialized advisory firm, combining deep expertise in sourcing, operating models and large-scale transformations."
    },
    cioAdvisory: {
      title: language === 'fr' ? 'CIO Advisory' : 'CIO Advisory',
      subtitle: language === 'fr' 
        ? "Accompagner les CIOs dans leurs décisions de transformation les plus critiques"
        : "Supporting CIOs in the most critical transformation decisions",
      description: language === 'fr'
        ? "Les leaders technologiques font face aujourd'hui à un ensemble croissant de défis stratégiques :"
        : "Technology leaders today face a growing set of strategic challenges:",
      challenges: language === 'fr' ? [
        "Concevoir des modèles opérationnels alignés avec la stratégie business",
        "Structurer des écosystèmes fournisseurs complexes",
        "Sécuriser les transitions d'outsourcing",
        "Délivrer de grands programmes de transformation",
        "Conduire le changement organisationnel à travers l'IT"
      ] : [
        "Designing operating models aligned with business strategy",
        "Structuring complex vendor ecosystems",
        "Securing outsourcing transitions",
        "Delivering large transformation programs",
        "Driving organizational change across IT"
      ],
      support: language === 'fr'
        ? "Walaby accompagne les CIOs dans ces défis en fournissant un conseil indépendant, pragmatique et orienté exécution. Notre rôle est d'aider les CIOs à traduire l'ambition stratégique en réalité opérationnelle."
        : "Walaby supports CIOs in navigating these challenges by providing independent, pragmatic and execution-oriented advisory. Our role is to help CIOs translate strategic ambition into operational reality."
    },
    approach: {
      title: language === 'fr' ? 'Notre Approche' : 'Our Approach',
      subtitle: language === 'fr'
        ? "Expertise focalisée avec conseil niveau exécutif"
        : "Focused expertise with executive-level advisory",
      intro: language === 'fr'
        ? "Contrairement aux grands cabinets de conseil, Walaby opère comme un cabinet de conseil focalisé, travaillant étroitement avec les équipes de direction pour adresser leurs défis les plus critiques. Ce modèle nous permet de fournir :"
        : "Unlike large consulting firms, Walaby operates as a focused advisory firm, working closely with executive leadership teams to address their most critical challenges. This model enables us to provide:",
      pillars: [
        {
          icon: Target,
          title: language === 'fr' ? 'Spécialisation profonde' : 'Deep specialization',
          description: language === 'fr'
            ? "Nous nous concentrons exclusivement sur le sourcing IT, les modèles opérationnels et la gouvernance de transformation, nous permettant d'apporter des insights hautement spécialisés."
            : "We focus exclusively on IT sourcing, operating models and transformation governance, enabling us to bring highly specialized insights."
        },
        {
          icon: Shield,
          title: language === 'fr' ? 'Conseil indépendant' : 'Independent advisory',
          description: language === 'fr'
            ? "Nous opérons comme conseiller de confiance des CIOs, fournissant un guidage objectif à travers les écosystèmes fournisseurs et initiatives de transformation."
            : "We operate as a trusted advisor to CIOs, providing objective guidance across vendor ecosystems and transformation initiatives."
        },
        {
          icon: Lightbulb,
          title: language === 'fr' ? 'Conseil pragmatique' : 'Pragmatic consulting',
          description: language === 'fr'
            ? "Nos recommandations sont ancrées dans les réalités opérationnelles et l'expérience de transformation du monde réel."
            : "Our recommendations are grounded in operational realities and real-world transformation experience."
        },
        {
          icon: Award,
          title: language === 'fr' ? 'Engagement senior' : 'Senior engagement',
          description: language === 'fr'
            ? "Les engagements Walaby sont menés par des consultants expérimentés avec un track record solide dans les transformations IT complexes et stratégies de sourcing."
            : "Walaby engagements are led by experienced advisors with a strong track record in complex IT transformations and sourcing strategies."
        }
      ]
    },
    mission: {
      title: language === 'fr' ? 'Notre Mission' : 'Our Mission',
      description: language === 'fr'
        ? "Walaby aide les CIOs et équipes de direction à concevoir des modèles opérationnels, structurer des écosystèmes fournisseurs et conduire des transformations complexes."
        : "Walaby helps CIOs and executive teams design operating models, structure vendor ecosystems and lead complex transformations.",
      ambition: language === 'fr'
        ? "Notre ambition est simple : S'assurer que les initiatives de transformation technologique délivrent une valeur business durable."
        : "Our ambition is simple: To ensure technology transformation initiatives deliver sustainable business value."
    },
    pillars: [
      {
        icon: Handshake,
        title: 'Partnership',
        description: language === 'fr' 
          ? "Nous construisons des relations durables basées sur la confiance et l'engagement mutuel."
          : "We build lasting relationships based on trust and mutual commitment."
      },
      {
        icon: Zap,
        title: 'Agility',
        description: language === 'fr'
          ? "Notre structure nous permet d'adapter nos interventions avec réactivité et flexibilité."
          : "Our structure allows us to adapt our interventions with responsiveness and flexibility."
      },
      {
        icon: Target,
        title: 'Pragmatism',
        description: language === 'fr'
          ? "Nous privilégions les solutions concrètes et opérationnelles, orientées résultats."
          : "We favor concrete and operational solutions, results-oriented."
      }
    ],
    stats: [
      { value: '2020', label: language === 'fr' ? 'Création' : 'Founded' },
      { value: '5+', label: language === 'fr' ? "Années d'expérience" : 'Years of experience' },
      { value: '30+', label: language === 'fr' ? 'Projets réalisés' : 'Completed projects' },
      { value: '10+', label: language === 'fr' ? 'Pays' : 'Countries' }
    ]
  };

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="who-we-are-page">
      {/* Hero */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="font-heading text-sm font-semibold tracking-[0.2em] text-[#E5A81D] mb-4 block animate-fade-in-up">
                {content.hero.subtitle}
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-[#0F172A] mb-6 animate-fade-in-up animation-delay-100">
                {content.hero.title}
              </h1>
              <p className="font-heading text-xl text-[#E5A81D] font-medium mb-6 animate-fade-in-up animation-delay-200">
                {content.hero.tagline}
              </p>
            </div>
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-[#E5A81D]/10 -z-10" />
                <img
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&q=80"
                  alt="Walaby Consulting team"
                  className="w-full h-[450px] object-cover shadow-hover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl">
            <p className="font-body text-lg text-slate-700 leading-relaxed mb-6">
              {content.intro.description}
            </p>
            <p className="font-body text-slate-600 leading-relaxed mb-6">
              {content.intro.context}
            </p>
            <p className="font-body text-slate-600 leading-relaxed">
              {content.intro.positioning}
            </p>
          </div>
        </div>
      </section>

      {/* CIO Advisory */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0F172A] mb-4">
            {content.cioAdvisory.title}
          </h2>
          <p className="font-heading text-xl text-slate-600 mb-8">
            {content.cioAdvisory.subtitle}
          </p>
          <p className="font-body text-slate-600 mb-6">
            {content.cioAdvisory.description}
          </p>
          <ul className="space-y-3 mb-8">
            {content.cioAdvisory.challenges.map((challenge, index) => (
              <li key={index} className="flex items-start space-x-3">
                <CheckCircle size={20} className="text-[#E5A81D] flex-shrink-0 mt-0.5" />
                <span className="font-body text-slate-700">{challenge}</span>
              </li>
            ))}
          </ul>
          <p className="font-body text-slate-700 font-medium">
            {content.cioAdvisory.support}
          </p>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0F172A] mb-4">
            {content.approach.title}
          </h2>
          <p className="font-heading text-xl text-slate-600 mb-6">
            {content.approach.subtitle}
          </p>
          <p className="font-body text-slate-600 mb-12 max-w-3xl">
            {content.approach.intro}
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {content.approach.pillars.map((pillar, index) => (
              <div 
                key={index}
                className="bg-white p-8 border border-slate-200 hover:border-[#E5A81D]/50 transition-all duration-300 hover:shadow-hover"
                data-testid={`approach-pillar-${index}`}
              >
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-[#E5A81D]/10 flex-shrink-0">
                    <pillar.icon size={24} className="text-[#E5A81D]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-[#0F172A] mb-2">
                      {pillar.title}
                    </h3>
                    <p className="font-body text-slate-600 text-sm leading-relaxed">
                      {pillar.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
            {content.mission.title}
          </h2>
          <p className="font-body text-lg text-slate-300 mb-4 max-w-3xl mx-auto">
            {content.mission.description}
          </p>
          <p className="font-heading text-xl text-[#E5A81D] font-medium">
            {content.mission.ambition}
          </p>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-[#0F172A]">
              Partnership, Agility & Pragmatism
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {content.pillars.map((pillar, index) => (
              <div 
                key={index}
                className="text-center p-8 bg-white border border-slate-100 hover:border-[#E5A81D]/50 transition-all duration-300 hover:shadow-hover animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`pillar-${index}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#E5A81D]/10 mb-6">
                  <pillar.icon size={32} className="text-[#E5A81D]" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-[#0F172A] mb-4">
                  {pillar.title}
                </h3>
                <p className="font-body text-slate-600 leading-relaxed">
                  {pillar.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {content.stats.map((stat, index) => (
              <div 
                key={index}
                className="text-center animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="font-heading text-4xl md:text-5xl font-bold text-[#E5A81D] mb-2">
                  {stat.value}
                </div>
                <div className="font-body text-sm text-slate-600">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="font-heading text-sm font-semibold tracking-[0.2em] text-[#E5A81D] mb-4 block">
                FRANCE
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-[#0F172A] mb-6">
                {language === 'fr' ? 'Notre siège' : 'Our headquarters'}
              </h2>
              <div className="font-body text-slate-600 space-y-4">
                <p className="text-lg">22 rue Eugène Givors</p>
                <p className="text-lg">94240 L'Haÿ-les-Roses</p>
                <p className="text-lg">France</p>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center mt-8 font-heading text-sm font-medium text-[#0F172A] hover:text-[#E5A81D] transition-colors"
                data-testid="location-contact"
              >
                {language === 'fr' ? 'Prendre rendez-vous' : 'Book an appointment'}
                <ArrowRight size={18} className="ml-2" />
              </Link>
            </div>
            <div>
              <div className="relative">
                <div className="absolute -inset-4 bg-[#0F172A]/5 -z-10" />
                <img
                  src="https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=600&q=80"
                  alt="Paris, France"
                  className="w-full h-[350px] object-cover shadow-card"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-[#0F172A] mb-6">
            {language === 'fr' ? 'PARLONS DE VOTRE PROJET' : "LET'S TALK ABOUT YOUR PROJECT"}
          </h2>
          <p className="font-body text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
            {language === 'fr' 
              ? 'Prêt à transformer votre organisation ? Contactez-nous pour discuter de vos besoins.'
              : 'Ready to transform your organization? Contact us to discuss your needs.'}
          </p>
          <Link
            to="/contact"
            data-testid="about-cta"
            className="inline-flex items-center justify-center bg-[#E5A81D] text-[#0F172A] px-8 py-4 font-heading text-sm font-bold hover:bg-[#E5A81D]/90 hover:shadow-lg transition-all duration-300"
          >
            {language === 'fr' ? 'Prendre rendez-vous' : 'Book an appointment'}
            <ArrowRight size={18} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WhoWeAre;
